import { useState, useCallback, useMemo } from 'react';
import { totalCheckboxSelected } from '../../utils/checkbox';

const convertFromValueToMap = (defaultValue) => {
  if (defaultValue.all) {
    return { checked: {}, allChecked: true };
  }

  if (defaultValue.include) {
    const checked = defaultValue.include.reduce((prev, key) => {
      prev[key] = true;
      return prev;
    }, {});

    return { checked, allChecked: false };
  }

  if (defaultValue.exclude) {
    const checked = defaultValue.exclude.reduce((prev, key) => {
      prev[key] = true;
      return prev;
    }, {});

    return { checked, allChecked: true };
  }

  return { checked: {}, allChecked: false };
};

const useCheckbox = ({ defaultChecked = {}, total = 0, disabled = [], disabledTooltip = '' } = {}) => {
  const { checked: defaultCheckedValue, allChecked } = convertFromValueToMap(defaultChecked);

  const [checked, setChecked] = useState(defaultCheckedValue);
  const [isAllChecked, setIsAllChecked] = useState(allChecked);

  const isChecked = useCallback((rowData) => checked[rowData.id], [checked]);
  const toggleCheck = useCallback((rowData) => setChecked({ ...checked, [rowData.id]: !checked[rowData.id] }), [
    checked,
    setChecked
  ]);

  const toggleAll = useCallback(
    (value) => {
      setIsAllChecked(value);
      setChecked({});
    },
    [setChecked]
  );

  const value = useMemo(() => {
    if (!isAllChecked) {
      const include = Object.entries(checked)
        .filter(([, val]) => val)
        .map(([key]) => parseInt(key, 10));

      return include.length === 0 ? {} : { include };
    }

    const exclude = Object.entries(checked)
      .filter(([, val]) => val)
      .map(([key]) => parseInt(key, 10));

    if (exclude.length > 0) {
      return { exclude };
    }

    return { all: true };
  }, [checked, isAllChecked]);

  const totalSelected = useMemo(() => totalCheckboxSelected({ value, total }), [value, total]);

  return { isChecked, toggleCheck, isAllChecked, toggleAll, value, totalSelected, disabled, disabledTooltip };
};

export default useCheckbox;
